import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ChevronDown, ShoppingBag, Truck, X } from 'lucide-react';

export default function FloatingCart() {
  const { cart, calculateTotals, isTakeaway, setIsTakeaway } = useApp();
  const navigate = useNavigate();
  const location = useLocation(); // 👈 get current route
  const [isOrderTypeOpen, setIsOrderTypeOpen] = useState(false);

  useEffect(() => {
    if (!isOrderTypeOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOrderTypeOpen(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOrderTypeOpen]);

  const selectTakeaway = () => {
    setIsTakeaway(true);
    setIsOrderTypeOpen(false);
  };

  const selectDelivery = () => {
    setIsTakeaway(false);
    setIsOrderTypeOpen(false);
    window.dispatchEvent(new CustomEvent('open-delivery-address-selector'));
  };

  // Hide button on cart page
  if (location.pathname === '/cart') return null;

  // Count total items
  const totalItems = Object.values(cart).reduce(
    (sum, daily) => sum + Object.values(daily).reduce((s, q) => s + q, 0),
    0
  );

  console.log('FloatingCart: totalItems =', totalItems);
  console.log('FloatingCart: cart =', cart);

  if (totalItems === 0) {
    if (location.pathname !== '/home' && location.pathname !== '/order') return null;
  } else {
    // Hide on login-related pages when there are items
    if (location.pathname === '/' || location.pathname === '/password' || location.pathname === '/otpverify' || location.pathname === '/newpassword' || location.pathname === '/register' || location.pathname === '/register/form') {
      return null;
    }
  }

  const totals = calculateTotals('0-5');
  console.log('FloatingCart: calculateTotals result =', totals);
  const { total } = totals;

  return (
    <div className={`fixed bottom-20 left-3 right-3 mx-auto w-fit max-w-[calc(100vw-20px)] md:bottom-6 md:left-auto md:right-6 md:mx-0 ${isOrderTypeOpen ? 'z-[60]' : 'z-20'}`}>
      <div className="flex items-center justify-between gap-2 p-1 overflow-hidden bg-red-500 border border-red-400 rounded-full shadow-lg shadow-red-300/30">
        {/* Order type selector */}
        <button
          type="button"
          onClick={() => setIsOrderTypeOpen(true)}
          className="inline-flex items-center justify-center flex-shrink-0 gap-1.5 min-w-[122px] px-3.5 py-2.5 text-sm font-medium text-red-500 transition-all bg-white rounded-full shadow-md sm:min-w-[146px] sm:px-4 sm:text-base hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/80"
          aria-haspopup="dialog"
          aria-expanded={isOrderTypeOpen}
        >
          <span className="text-base leading-none sm:text-lg" aria-hidden="true">
            {isTakeaway ? "🛍️" : "🛵"}
          </span>
          {isTakeaway ? 'Takeaway' : 'Delivery'}
          <ChevronDown size={14} strokeWidth={2.5} />
        </button>

        {/* Floating Cart Button */}
        <button
          onClick={() => navigate('/cart')}
          className="flex items-center justify-end flex-1 self-stretch min-w-0 px-1 pr-2 text-sm font-black text-white transition-transform rounded-full sm:pr-3 sm:text-base hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/80"
          aria-label={`View cart, ${totalItems} items, ₹${total.toFixed(2)}`}
        >
          <span className="truncate whitespace-nowrap">{totalItems} items · ₹{total.toFixed(2)}</span>
        </button>
      </div>

      {isOrderTypeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
          onClick={() => setIsOrderTypeOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-type-title"
            className="w-full max-w-sm max-h-[calc(100vh-32px)] overflow-y-auto bg-white shadow-2xl rounded-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 text-white bg-red-500">
              <div>
                <p className="text-[11px] font-bold tracking-wide uppercase text-white/80">Order preference</p>
                <h3 id="order-type-title" className="text-lg font-bold">How would you like your order?</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsOrderTypeOpen(false)}
                className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full hover:bg-white/15"
                aria-label="Close order type selector"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-3 p-5 sm:grid-cols-2">
              <button
                type="button"
                onClick={selectTakeaway}
                className={`flex flex-col items-center gap-2 p-5 text-center transition-all border-2 rounded-xl hover:-translate-y-0.5 hover:shadow-md ${
                  isTakeaway ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white hover:border-teal-300'
                }`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 text-teal-700 bg-teal-100 rounded-full">
                  <ShoppingBag size={23} />
                </span>
                <span className="font-bold text-gray-900">Takeaway</span>
                <span className="text-xs leading-relaxed text-gray-500">Pick up your order and apply takeaway rates.</span>
              </button>

              <button
                type="button"
                onClick={selectDelivery}
                className={`flex flex-col items-center gap-2 p-5 text-center transition-all border-2 rounded-xl hover:-translate-y-0.5 hover:shadow-md ${
                  !isTakeaway ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-red-300'
                }`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 text-red-600 bg-red-100 rounded-full">
                  <Truck size={23} />
                </span>
                <span className="font-bold text-gray-900">Delivery</span>
                <span className="text-xs leading-relaxed text-gray-500">Choose an address and apply its delivery rate.</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
